/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import {getCountryName} from '../common/directives/country-select/countries';

export {
  APPROVAL_PREVIEW_INITIAL_CREATIVE_COUNT,
  APPROVAL_PREVIEW_MAX_CREATIVE_BATCH_SIZE,
  getApprovalPreviewCreativeBatchSize
} from '../site/site-commons';

export const AX_PUBLISHER_CONTACT_USER_PERMISSIONS = ['AX_PUBLISHER'];

export function cleanseDataToArray(sizeString: string | undefined | null): [number | undefined, number | undefined] {
  const theData = (sizeString || '').split('x');
  const firstData = parseInt(String(_.get(theData, [0], -1)), 10);
  const secondData = parseInt(String(_.get(theData, [1], -1)), 10);
  return _.isFinite(firstData) && firstData > 0 && _.isFinite(secondData) && secondData > 0
    ? [firstData, secondData]
    : [undefined, undefined];
}

export interface FullPageDimensions {
  desktopWidth?: number;
  desktopHeight?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  topScrollWidth?: number;
  topScrollHeight?: number;
  midScrollWidth?: number;
  midScrollHeight?: number;
  doubleFullWidth?: number;
  doubleFullHeight?: number;
  mobileTopScrollWidth?: number;
  mobileTopScrollHeight?: number;
  mobileMidScrollWidth?: number;
  mobileMidScrollHeight?: number;
  mobileDoubleFullWidth?: number;
  mobileDoubleFullHeight?: number;
}

export function loadFullPageDimensionsFromModel(model: any): FullPageDimensions {
  const dims: FullPageDimensions = {};
  const desktopArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'DESKTOP']));
  dims.desktopWidth = desktopArray[0];
  dims.desktopHeight = desktopArray[1];

  const mobileArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'MOBILE']));
  dims.mobileWidth = mobileArray[0];
  dims.mobileHeight = mobileArray[1];

  const topScrollArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'TOP_SCROLL']));
  dims.topScrollWidth = topScrollArray[0];
  dims.topScrollHeight = topScrollArray[1];

  const midScrollArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'MID_SCROLL']));
  dims.midScrollWidth = midScrollArray[0];
  dims.midScrollHeight = midScrollArray[1];

  const doubleFullArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'DOUBLE_FULLSCREEN']));
  dims.doubleFullWidth = doubleFullArray[0];
  dims.doubleFullHeight = doubleFullArray[1];

  const mobileTopScrollArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'MOBILE_TOP_SCROLL']));
  dims.mobileTopScrollWidth = mobileTopScrollArray[0];
  dims.mobileTopScrollHeight = mobileTopScrollArray[1];

  const mobileMidScrollArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'MOBILE_MID_SCROLL']));
  dims.mobileMidScrollWidth = mobileMidScrollArray[0];
  dims.mobileMidScrollHeight = mobileMidScrollArray[1];

  const mobileDoubleFullArray = cleanseDataToArray(_.get(model, ['fullPageSizes', 'MOBILE_DOUBLE_FULLSCREEN']));
  dims.mobileDoubleFullWidth = mobileDoubleFullArray[0];
  dims.mobileDoubleFullHeight = mobileDoubleFullArray[1];

  return dims;
}

export function applyFullPageSizesToModel(model: any, dims: FullPageDimensions): void {
  model.fullPageSizes = model.fullPageSizes || {};

  if (_.isFinite(dims.desktopWidth) && (dims.desktopWidth as number) > 0
    && _.isFinite(dims.desktopHeight) && (dims.desktopHeight as number) > 0) {
    model.fullPageSizes.DESKTOP = dims.desktopWidth + 'x' + dims.desktopHeight;
  } else if (_.get(model, ['fullPageSizes', 'DESKTOP'])) {
    delete model.fullPageSizes.DESKTOP;
  }

  if (_.isFinite(dims.mobileWidth) && (dims.mobileWidth as number) > 0
    && _.isFinite(dims.mobileHeight) && (dims.mobileHeight as number) > 0) {
    model.fullPageSizes.MOBILE = dims.mobileWidth + 'x' + dims.mobileHeight;
  } else if (_.get(model, ['fullPageSizes', 'MOBILE'])) {
    delete model.fullPageSizes.MOBILE;
  }

  if (_.isFinite(dims.topScrollWidth) && (dims.topScrollWidth as number) > 0
    && _.isFinite(dims.topScrollHeight) && (dims.topScrollHeight as number) > 0) {
    model.fullPageSizes.TOP_SCROLL = dims.topScrollWidth + 'x' + dims.topScrollHeight;
  } else if (_.get(model, ['fullPageSizes', 'TOP_SCROLL'])) {
    delete model.fullPageSizes.TOP_SCROLL;
  }

  if (_.isFinite(dims.midScrollWidth) && (dims.midScrollWidth as number) > 0
    && _.isFinite(dims.midScrollHeight) && (dims.midScrollHeight as number) > 0) {
    model.fullPageSizes.MID_SCROLL = dims.midScrollWidth + 'x' + dims.midScrollHeight;
  } else if (_.get(model, ['fullPageSizes', 'MID_SCROLL'])) {
    delete model.fullPageSizes.MID_SCROLL;
  }

  if (_.isFinite(dims.doubleFullWidth) && (dims.doubleFullWidth as number) > 0
    && _.isFinite(dims.doubleFullHeight) && (dims.doubleFullHeight as number) > 0) {
    model.fullPageSizes.DOUBLE_FULLSCREEN = dims.doubleFullWidth + 'x' + dims.doubleFullHeight;
  } else if (_.get(model, ['fullPageSizes', 'DOUBLE_FULLSCREEN'])) {
    delete model.fullPageSizes.DOUBLE_FULLSCREEN;
  }

  if (_.isFinite(dims.mobileTopScrollWidth) && (dims.mobileTopScrollWidth as number) > 0
    && _.isFinite(dims.mobileTopScrollHeight) && (dims.mobileTopScrollHeight as number) > 0) {
    model.fullPageSizes.MOBILE_TOP_SCROLL = dims.mobileTopScrollWidth + 'x' + dims.mobileTopScrollHeight;
  } else if (_.get(model, ['fullPageSizes', 'MOBILE_TOP_SCROLL'])) {
    delete model.fullPageSizes.MOBILE_TOP_SCROLL;
  }

  if (_.isFinite(dims.mobileMidScrollWidth) && (dims.mobileMidScrollWidth as number) > 0
    && _.isFinite(dims.mobileMidScrollHeight) && (dims.mobileMidScrollHeight as number) > 0) {
    model.fullPageSizes.MOBILE_MID_SCROLL = dims.mobileMidScrollWidth + 'x' + dims.mobileMidScrollHeight;
  } else if (_.get(model, ['fullPageSizes', 'MOBILE_MID_SCROLL'])) {
    delete model.fullPageSizes.MOBILE_MID_SCROLL;
  }

  if (_.isFinite(dims.mobileDoubleFullWidth) && (dims.mobileDoubleFullWidth as number) > 0
    && _.isFinite(dims.mobileDoubleFullHeight) && (dims.mobileDoubleFullHeight as number) > 0) {
    model.fullPageSizes.MOBILE_DOUBLE_FULLSCREEN = dims.mobileDoubleFullWidth + 'x' + dims.mobileDoubleFullHeight;
  } else if (_.get(model, ['fullPageSizes', 'MOBILE_DOUBLE_FULLSCREEN'])) {
    delete model.fullPageSizes.MOBILE_DOUBLE_FULLSCREEN;
  }
}

export function expandCountries(model: any): string {
  return _.map(model.countries || [], (country: string) => {
    return getCountryName(country);
  }).join(', ');
}

const TRANSPARENCY_LANGUAGE_BY_COUNTRY: Record<string, string> = {
  NO: 'no',
  DK: 'da',
  SE: 'sv',
  FI: 'fi',
  DE: 'de',
  AT: 'de',
  CH: 'de'
};

export function transparencyLanguageFromEarningsAccount(model: any): string | undefined {
  const countries = model?.countries;
  const country = (_.isArray(countries) && countries.length)
    ? countries[0]
    : _.get(model, ['address', 'country']);
  if (!country) {
    return undefined;
  }
  return TRANSPARENCY_LANGUAGE_BY_COUNTRY[String(country).toUpperCase()] || 'en';
}
